import { useEffect, useState, type ReactNode } from 'react';
import { AppShell } from '@noor/ui';
import { AppSidebar } from './app-sidebar';
import { DashboardHeader } from './dashboard-header';

interface AppShellLayoutProps {
  /** The routed page content (an `<Outlet />`). */
  children: ReactNode;
}

/**
 * The authenticated app chrome, matching the Figma full-height dark sidebar
 * layout: a flush 240px/64px sidebar on the left edge, and the main
 * application column filling the rest of the row — a flush full-width header
 * on top, the 20px padded page content below it.
 *
 * - Desktop sidebar remembers its expanded state for the life of the tab.
 * - Mobile sidebar opens as a 240px navigation drawer from the header.
 * - Top header (`DashboardHeader`, Figma 1212:8200) is permanently part of the layout.
 */
export function AppShellLayout({ children }: AppShellLayoutProps) {
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  useEffect(() => {
    if (!mobileSidebarOpen) return;
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setMobileSidebarOpen(false);
    };
    window.addEventListener('keydown', closeOnEscape);
    return () => window.removeEventListener('keydown', closeOnEscape);
  }, [mobileSidebarOpen]);

  return (
    <AppShell.Root className="h-dvh overflow-hidden bg-background">
      <AppSidebar className="hidden md:flex" />
      {mobileSidebarOpen && (
        <>
          <button
            type="button"
            aria-label="Закрыть меню"
            className="fixed inset-0 z-40 bg-black/50 md:hidden"
            onClick={() => setMobileSidebarOpen(false)}
          />
          <AppSidebar
            mobile
            className="md:hidden"
            onClose={() => setMobileSidebarOpen(false)}
          />
        </>
      )}
      <AppShell.Main className="min-w-0 overflow-hidden rounded-none">
        <DashboardHeader onOpenSidebar={() => setMobileSidebarOpen(true)} />
        <div className="flex min-h-0 flex-1 flex-col overflow-hidden p-5">{children}</div>
      </AppShell.Main>
    </AppShell.Root>
  );
}
